import { format } from 'date-fns';
import { transformDocumentNumber } from '@/components/custom/erp';
import { MA_CHUNG_TU } from '@/constants';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: MuaHangRecord[]) => {
  return detailRows.map((row: MuaHangRecord, index: number) => ({
    line: index + 1,
    ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
    ten_vt: row.ma_vt_data?.ten_vt || row.ten_vt || '',
    dvt: row.ma_vt_data?.dvt || row.dvt || '',
    ten_dvt: row.dvt_data?.ten_dvt || row.ma_vt_data?.dvt_data?.ten_dvt || row.dvt || '',
    ma_kho: row.ma_kho_data?.uuid || row.ma_kho || '',
    ten_kho: row.ma_kho_data?.ten_kho || row.ma_kho || '',
    ma_lo: row.ma_lo_data?.uuid || row.ma_lo || '',
    lo_yn: row.lo_yn || false,
    ma_vi_tri: row.ma_vi_tri_data?.uuid || row.ma_vi_tri || '',
    vi_tri_yn: row.vi_tri_yn || false,
    he_so: row.he_so || 1,
    qc_yn: row.qc_yn || false,
    so_luong: row.so_luong || 0,
    gia_nt: row.gia_nt || 0,
    tien_nt: row.tien_nt || 0,
    tien0: row.tien0 || 0,
    tien2: row.tien2 || 0,
    tien3: row.tien3 || 0,
    cp_nt: row.cp_nt || 0,
    thue_nt: row.thue_nt || 0,
    gia: row.gia || 0,
    tien: row.tien || 0,
    thue: row.thue || 0,
    tk_vt: row.tk_vt_data?.uuid || row.ma_vt_data?.tk_khcp_data?.uuid || '',
    ten_tk_vt: row.tk_vt_data?.name || row.tk_vt || '',
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || null,
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || '',
    id_hd: row.id_hd?.uuid || null,
    line_hd: row.line_hd?.uuid || null
  }));
};

/**
 * Transform tax rows for API submission
 * @param taxRows - Array of tax row data from the form
 * @returns Transformed tax rows ready for API submission
 */
export const transformTaxRows = (taxRows: MuaHangRecord[], state: FormFieldState, dien_giai: string) => {
  return taxRows.map((row: MuaHangRecord, index: number) => ({
    line: row.line || index + 1,
    so_ct0: row.so_ct0 || '',
    so_ct2: row.so_ct2 || '',
    ngay_ct0: row.ngay_ct0 || new Date().toISOString().split('T')[0] || '',
    ma_thue: row.ma_thue_data?.uuid || row.ma_thue || '',
    thue_suat: row.ma_thue_data?.thue_suat || row.thue_suat || 0,
    ma_tc_thue: row.ma_tc_thue_data?.uuid || '',
    ma_mau_ct: row.ma_mau_ct_data?.uuid || '',
    ma_mau_bc: row.ma_mau_bc || '',
    ma_kh: row.ma_kh_data?.uuid || state.nhaCungCap?.uuid || '',
    ten_kh_thue: row.ma_kh_data?.customer_name || row.ten_kh_thue || state.nhaCungCap?.customer_name || '',
    dia_chi: row.dia_chi || row.ma_kh_data?.address || state.nhaCungCap?.address || '',
    ma_so_thue: row.ma_so_thue || row.ma_kh_data?.tax_code || state.nhaCungCap?.tax_code || '',
    ten_vt_thue: row.ten_vt_thue || dien_giai,
    t_tien_nt: row.t_tien_nt || 0,
    t_thue_nt: row.t_thue_nt || 0,
    t_tien: row.t_tien || 0,
    t_thue: row.t_thue || 0,
    tk_thue_no: row.tk_thue_no_data?.uuid || row.ma_thue_data?.tk_thue_dau_vao_data?.uuid || '',
    ten_tk_thue_no: row.tk_thue_no_data?.name || row.ma_thue_data?.tk_thue_dau_vao_data?.name || '',
    ma_tt: row.ma_tt_data?.uuid || row.ma_tt || '',
    ma_kh9: row.ma_kh9_data?.uuid || row.ma_kh9 || '',
    ghi_chu: row.ghi_chu || '',
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || ''
  }));
};

/**
 * Transform fee rows for API submission
 * @param feeRows - Array of fee row data from the form
 * @returns Transformed fee rows ready for API submission
 */
export const transformFeeRows = (feeRows: MuaHangRecord[]) => {
  return feeRows.map((row: MuaHangRecord, index: number) => ({
    line: index + 1,
    ma_cp: row.ma_cp_data?.uuid || row.ma_cp || '',
    tien_cp: row.tien_cp_nt || 0,
    tien_cp_nt: row.tien_cp_nt || 0,
    loai_pb: row.ma_cp_data?.loai_pb || row.loai_pb || '1'
  }));
};

/**
 * Transform fee detail rows for API submission
 * @param feeDetailRows - Array of fee detail row data from the form
 * @returns Transformed fee detail rows ready for API submission
 */
export const transformFeeDetailRows = (feeDetailRows: MuaHangRecord[]) => {
  return feeDetailRows.map((row: MuaHangRecord, index: number) => ({
    line: index + 1,
    ma_cp: row.ma_cp_data?.uuid || row.ma_cp || '',
    ten_cp: row.ma_cp_data?.ten_cp || row.ten_cp || '',
    ten_cp_nt: row.ten_cp_nt || 0,
    line_vt: row.line_vt || index + 1
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param taxRows - Array of tax row data
 * @param feeRows - Array of fee row data
 * @param feeDetailRows - Array of fee detail row data
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: MuaHangRecord,
  state: FormFieldState,
  detailRows: MuaHangRecord[] = [],
  taxRows: MuaHangRecord[] = [],
  feeRows: MuaHangRecord[] = [],
  feeDetailRows: MuaHangRecord[] = [],
  entityUnit: MuaHangRecord,
  bottomBar: MuaHangRecord
) => {
  const chi_tiet = transformDetailRows(detailRows);
  const thong_tin_thue = transformTaxRows(taxRows, state, data.dien_giai);
  const chi_phi = transformFeeRows(feeRows);
  const chi_phi_chi_tiet = transformFeeDetailRows(feeDetailRows);

  return {
    ...data,
    unit_id: entityUnit?.uuid || '',
    transfer_yn: state.transfer_yn,
    lo_yn: state.lo_yn,
    vi_tri_yn: state.vi_tri_yn,
    qc_yn: state.qc_yn,
    ma_ngv: '3',
    ma_kh: state.nhaCungCap?.uuid || '',
    ten_kh: state.nhaCungCap?.customer_name || '',
    ten_kh_thue: data.ten_kh_thue || state.nhaCungCap?.customer_name || '',
    ma_so_thue: data.ma_so_thue || state.nhaCungCap?.tax_code || '',
    dia_chi: data.dia_chi || state.nhaCungCap?.address || '',
    e_mail: data.e_mail || state.nhaCungCap?.email || '',
    ong_ba: data.ong_ba || state.nhaCungCap?.contact_person || '',
    dien_giai: data.dien_giai || state.nhaCungCap?.description || '',
    ma_nx: state.nhapXuat?.uuid || '',
    ma_nvbh: state.nhapXuat?.uuid || '',
    tk: state.taiKhoan?.uuid || state.nhaCungCap?.account_data?.uuid || '',
    ma_tt: state.hanThanhToan?.uuid || state.nhaCungCap?.payment_term_data?.uuid || '',
    ma_nt: data.ma_nt || '',
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),
    ma_thue: state.taxModel?.uuid || '',
    tk_thue: state.taiKhoanThue?.uuid || '',
    thue_suat: state.taxModel?.thue_suat || 0,
    ngay_ct: format(data.ngay_ct, 'yyyy-MM-dd') || '',
    so_ct2: data.so_ct2 || '',
    so_ct0: data.so_ct0 || '',
    ngay_lct: format(data.ngay_ct, 'yyyy-MM-dd') || '',
    ngay_ct0: format(data.ngay_ct0, 'yyyy-MM-dd') || '',
    ma_gd: data.ma_gd || 'TN',
    t_cp_nt: bottomBar.t_cp || 0,
    t_cp: bottomBar.t_cp || 0,
    t_tt_nt: bottomBar.t_tt || 0,
    t_tt: bottomBar.t_tt || 0,
    t_thue_nt: bottomBar.t_thue || 0,
    t_tien_nt: data.t_tien_nt || 0,
    t_thue: bottomBar.t_thue || 0,
    t_tien: data.t_tien || 0,
    status: data.status || '5',
    ty_gia: data.ty_gia || 1,
    chi_tiet_phieu_nhaps: chi_tiet,
    thue_phieu_nhaps: thong_tin_thue,
    chi_phi_phieu_nhaps: chi_phi,
    chi_phi_chi_tiet_phieu_nhaps: chi_phi_chi_tiet
  };
};
